import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { joyApi } from "../api/joyApi";
import AppShell from "../components/AppShell";
import TopBar from "../components/TopBar";
import JoyButton from "../components/JoyButton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const TIMES = [
  { value: "08:00", label: "08:00 - Manhã ☀️" },
  { value: "12:00", label: "12:00 - Meio-dia 🌤️" },
  { value: "15:00", label: "15:00 - Tarde 🌻" },
  { value: "18:00", label: "18:00 - Fim da tarde 🌅" },
  { value: "20:00", label: "20:00 - Noite 🌙" },
];

export default function Settings() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const { userId, logout } = useAuth();

  const [enabled, setEnabled] = useState(false);
  const [time, setTime] = useState("18:00");

  const { data: reminder } = useQuery({
    queryKey: ["reminder", userId],
    queryFn: () => joyApi.getReminder(userId),
    enabled: !!userId,
  });

  useEffect(() => {
    if (reminder) {
      setEnabled(!!reminder.enabled);
      if (reminder.time) setTime(reminder.time);
    }
  }, [reminder]);

  const saveReminder = async (nextEnabled, nextTime) => {
    try {
      if (nextEnabled && "Notification" in window && Notification.permission === "default") {
        await Notification.requestPermission();
      }
      await joyApi.setReminder({ user_id: userId, enabled: nextEnabled, time: nextTime });
      toast.success("Preferências salvas! 💕");
    } catch (e) {
      toast.error("Não foi possível salvar agora. 🦊");
    }
  };

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    saveReminder(next, time);
  };

  const changeTime = (t) => {
    setTime(t);
    if (enabled) saveReminder(true, t);
  };

  const doReset = async () => {
    try {
      await joyApi.resetProgress(userId);
      qc.invalidateQueries();
      toast.success("Progresso resetado. Vamos recomeçar! 🌈");
      nav("/");
    } catch (e) {
      toast.error("Não foi possível resetar agora. 🦊");
    }
  };

  return (
    <AppShell>
      <TopBar title="Configurações ⚙️" to="/" testId="settings-topbar" />

      <div className="bg-white rounded-[22px] p-5 border-2 border-[#FFE0EC] mb-4">
        <div className="flex items-center justify-between">
          <span className="font-black text-[#4A3728]">🔔 Notificações</span>
          <button
            data-testid="reminder-toggle"
            onClick={toggle}
            className="joy-press w-14 h-8 rounded-full transition-colors"
            style={{ background: enabled ? "#4CAF50" : "#E7DCC4" }}
          >
            <span
              className="block w-6 h-6 bg-white rounded-full transition-transform"
              style={{ transform: enabled ? "translateX(26px)" : "translateX(4px)" }}
            />
          </button>
        </div>
        <p className="text-sm font-bold text-[#8B7355] mt-1">Lembrete diário para praticar com a Joy</p>

        {enabled && (
          <div className="grid grid-cols-1 gap-2 mt-4 joy-slide-in">
            {TIMES.map((t) => (
              <button
                key={t.value}
                data-testid={`reminder-time-${t.value}`}
                onClick={() => changeTime(t.value)}
                className={`joy-press rounded-[16px] px-4 py-3 font-bold text-left border-2 ${
                  time === t.value ? "border-[#FF69B4] bg-[#FFE0EC] text-[#4A3728]" : "border-[#FFEAD6] bg-white text-[#8B7355]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-[22px] p-5 border-2 border-[#FFE0EC] mb-4 text-center">
        <p className="font-black text-[#4A3728]">💕 Sobre</p>
        <img src="/joy.png" alt="Joy" className="w-16 h-16 rounded-full object-cover border-2 border-[#FF69B4] mx-auto my-3" />
        <p className="font-black text-[#FF69B4]">Joy's English Adventure</p>
        <p className="text-sm font-bold text-[#8B7355]">Versão 1.0.0</p>
        <p className="text-sm font-bold text-[#8B7355] mt-1">Aprenda inglês de forma divertida!</p>
      </div>

      <JoyButton variant="white" full className="mb-4" data-testid="logout-button" onClick={logout}>
        🚪 Sair da conta
      </JoyButton>

      <div className="rounded-[22px] p-5 border-2 border-[#F8C6D2] bg-[#FFF0F3]">
        <p className="font-black text-[#E91E63] mb-3">🗑️ Área de perigo</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <JoyButton variant="white" full className="border-[#E91E63] text-[#E91E63]" data-testid="reset-progress-button">
              🗑️ Resetar progresso
            </JoyButton>
          </AlertDialogTrigger>
          <AlertDialogContent className="rounded-[24px]">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-black text-[#4A3728]">Tem certeza? 🥺</AlertDialogTitle>
              <AlertDialogDescription className="font-semibold text-[#8B7355]">
                Isso vai apagar todo o seu XP, moedas, conquistas e lições completadas. Essa ação não pode ser desfeita.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reset-cancel" className="rounded-[16px] font-bold">Cancelar</AlertDialogCancel>
              <AlertDialogAction data-testid="reset-confirm" onClick={doReset} className="rounded-[16px] font-bold bg-[#E91E63]">
                Sim, resetar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppShell>
  );
}
